import { type JSX, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { readSession, writeLastActivity } from '../auth/session-store.ts'
import { Button } from '../design-system/button.tsx'
import { Notice } from '../design-system/notice.tsx'
import {
  IDLE_LIMIT_MS,
  IDLE_WARNING_EVENT,
  IDLE_WARNING_MS,
  formatSignOutCountdown,
  idlePhase,
  msUntilPhaseChange,
  secondsUntilSignOut
} from '../lib/idle.ts'

/**
 * What counts as being present.
 *
 * Deliberately short. `pointerdown` and `keydown` are intent; `mousemove` is
 * not — a nudged desk would keep a super session alive indefinitely, which is
 * the situation this guard exists for.
 */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown'] as const

/*
 * `keydown` auto-repeats while a key is held, so a book on the keyboard — or
 * a stuck key — would refresh the activity clock forever and the session
 * would never expire. A repeat is the keyboard talking, not a person, so
 * markActive ignores it.
 */

/** How often the countdown refreshes, inside the warning window only. */
const TICK_MS = 1000

/**
 * How often the activity stamp is written through to the store.
 *
 * `keydown` fires per character, and the stamp only has to survive a reload —
 * writing on every event would serialise the whole session key on each
 * keystroke to move a timestamp by milliseconds.
 */
const ACTIVITY_WRITE_MS = 5000

export interface IdleGuardProps {
  /**
   * Ends the session. May return a promise; the guard waits for it so a
   * sign-out that fails does not leave the guard disarmed.
   */
  onSignOut: () => void | Promise<unknown>
  limitMs?: number
  warningMs?: number
  /**
   * When true, the allowance does not sign the session out and the warning
   * stays down. A display account sets this. Anyone else leaves it false.
   */
  suspend?: boolean
}

/**
 * IdleGuard — signs an idle session out, warning first.
 *
 * A console holding internal tools should not sit signed in on an unattended
 * screen. Since ADR-0003 the session survives a reload, so this guard is no
 * longer the backstop behind one — it is one of the two limits a session ends
 * on, the other being the stored token's own `exp`. EI-446 raised this
 * allowance to the hour that token also lives for, so ADR-0003 is now the
 * place that records which limit ends which tab.
 *
 * **The allowance is seeded from the store and written back to it.** A reload
 * used to end the session, which made resetting the idle clock harmless. Now
 * that it does not, a reload would otherwise be a way to hand out a fresh
 * hour, quietly weakening the control this component applies. With no stored
 * stamp — a fresh sign-in — the allowance starts now.
 *
 * It sleeps until something is due rather than polling: one timer to the
 * warning boundary, then a one-second tick only while the countdown is
 * actually on screen. Polling every second would wake once per second of the
 * whole allowance, and display nothing for every second outside the warning.
 *
 * Elapsed time is the only state; the phase and the countdown are derived
 * from it, so they cannot disagree.
 *
 * The warning is portaled to `document.body` and announces itself on
 * `IDLE_WARNING_EVENT`, so a modal nav sheet cannot aria-hide it or trap
 * focus away from Stay.
 *
 * **The guard re-arms if the sign-out does not take.** Latching permanently on
 * the first attempt would mean a failed sign-out leaves a privileged session
 * open on an unattended screen with no timer and no banner — the exact case
 * this component exists to prevent. So the latch guards one attempt, not the
 * component's life: if the session is still mounted once the attempt settles,
 * the guard resumes inside the warning window, keeping the banner up and
 * retrying.
 *
 * @param props - IdleGuard props.
 * @param props.onSignOut - Called once when the idle allowance runs out.
 * @param props.limitMs - Idle allowance before sign-out.
 * @param props.warningMs - How long before sign-out to warn.
 * @param props.suspend - When true, the allowance does not sign the session out.
 * @returns The warning banner while warning, otherwise nothing.
 */
export function IdleGuard({
  onSignOut,
  limitMs = IDLE_LIMIT_MS,
  warningMs = IDLE_WARNING_MS,
  suspend = false
}: IdleGuardProps): JSX.Element | null {
  // Read once, in a state initializer rather than an effect, so the ref and
  // the state cannot disagree about when the operator was last seen — and so
  // a session already past its allowance is expired on the first render
  // instead of a whole interval later.
  const [seenAt] = useState(() => readSession()?.lastActivityAt ?? Date.now())
  const lastActivity = useRef(seenAt)
  // When the activity stamp was last written through, for the throttle.
  const lastWrite = useRef(seenAt)
  // Guards one sign-out attempt against re-entry, not the component's life.
  const signOutInFlight = useRef(false)
  const mounted = useRef(true)
  const [idleForMs, setIdleForMs] = useState(() =>
    Math.max(0, Date.now() - seenAt)
  )

  // Held in a ref so a fresh arrow from the parent cannot restart the timer.
  // App passes an inline callback, and a restart would reset the allowance.
  const signOutRef = useRef(onSignOut)
  useEffect(() => {
    signOutRef.current = onSignOut
  }, [onSignOut])

  const phase = idlePhase(idleForMs, limitMs, warningMs)
  const secondsLeft = secondsUntilSignOut(idleForMs, limitMs)

  /**
   * When the operator was last seen, in any tab.
   *
   * The stamp is origin-wide. Without reading it back, a second tab left open
   * idles out on its own schedule, signs out, and — because a sign-out is now
   * cross-tab — takes the tab the operator is actually working in with it.
   *
   * @returns The later of this tab's own activity and the stored stamp.
   */
  const lastSeen = useCallback((): number => {
    return Math.max(lastActivity.current, readSession()?.lastActivityAt ?? 0)
  }, [])

  const markActive = useCallback(() => {
    // While a sign-out is in flight, activity must not revive the banner or
    // extend the session it is trying to end.
    if (signOutInFlight.current) {
      return
    }
    const now = Date.now()
    lastActivity.current = now
    if (now - lastWrite.current >= ACTIVITY_WRITE_MS) {
      lastWrite.current = now
      writeLastActivity(now)
    }
    setIdleForMs(0)
  }, [])

  const onActivity = useCallback(
    (event: Event) => {
      if (event instanceof KeyboardEvent && event.repeat) {
        return
      }
      markActive()
    },
    [markActive]
  )

  useEffect(() => {
    mounted.current = true
    for (const event of ACTIVITY_EVENTS) {
      // Passive: the handler never cancels the event, and it now does real
      // work — it writes the activity stamp through to the store.
      document.addEventListener(event, onActivity, { passive: true })
    }

    return () => {
      mounted.current = false
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, onActivity)
      }
    }
  }, [onActivity])

  useEffect(() => {
    // A display account stays signed in. The effect still re-arms when
    // `suspend` goes back to false, so a person never inherits the exemption.
    if (suspend) {
      return undefined
    }
    if (phase === 'expired') {
      if (!signOutInFlight.current) {
        signOutInFlight.current = true
        void Promise.resolve(signOutRef.current())
          .catch(() => undefined)
          .finally(() => {
            signOutInFlight.current = false
            // Still here means the sign-out did not take. Resume just inside
            // the warning window so the banner stays up and expiry retries,
            // rather than silently granting another full allowance.
            if (mounted.current) {
              lastActivity.current = Date.now() - (limitMs - warningMs)
              setIdleForMs(limitMs - warningMs)
            }
          })
      }

      return undefined
    }

    // Inside the warning window the countdown needs a tick; outside it there
    // is nothing to show until the boundary, so sleep until then. Either way
    // the elapsed time is recomputed from the activity ref, so a timer left
    // over from before an activity reset simply wakes early and reschedules.
    const wait =
      phase === 'warning'
        ? TICK_MS
        : (msUntilPhaseChange(idleForMs, limitMs, warningMs) ?? TICK_MS)

    const id = setTimeout(() => {
      const seen = lastSeen()
      lastActivity.current = seen
      setIdleForMs(Date.now() - seen)
    }, wait)

    return () => clearTimeout(id)
  }, [idleForMs, phase, limitMs, warningMs, lastSeen, suspend])

  useEffect(() => {
    if (suspend || phase !== 'warning') {
      return
    }
    window.dispatchEvent(new Event(IDLE_WARNING_EVENT))
  }, [phase, suspend])

  if (suspend || phase !== 'warning') {
    return null
  }

  return createPortal(
    <div className="fixed inset-x-0 top-0 z-[60] p-3">
      {/*
        The visible countdown is not announced: it changes every second for
        the whole warning window, and an assertive region would read the
        warning out on each of them and swallow the Stay control with it. The
        polite one-shot below says it once.
      */}
      <Notice tone="warning" data-testid="idle-warning" announce={false}>
        <span
          role="status"
          aria-live="polite"
          data-testid="idle-warning-announcement"
          className="sr-only"
        >
          {'Your session is about to end. Choose Stay signed in to continue.'}
        </span>
        <span aria-hidden="true" className="flex flex-wrap items-center gap-3">
          {`Signing out in ${formatSignOutCountdown(secondsLeft)}.`}
        </span>
        <Button
          data-testid="idle-stay-btn"
          variant="quiet"
          size="sm"
          className="self-start"
          onClick={markActive}
        >
          {'Stay signed in'}
        </Button>
      </Notice>
    </div>,
    document.body
  )
}
